import { Response, JsonApiResponse } from "@stemscopes/api";
import { ElementPart, StudentAssignment } from "types";
import { generatePath, request } from "utils";
import { endpoints } from "configs";
import { getParams, getActualElement } from "./selectors";
import { STUDENT } from "./constants";
import { FetchFullAssignment, FetchFullDistrictAssignment } from "./types";

export const fetchStudentAssignment = async (
  id: string,
  isLti?: boolean,
  isStarted?: boolean,
  ltiSchoolKey?: string,
): Promise<Response<StudentAssignment>> => {
  const urlPath = !isLti
    ? endpoints.studentAssignment
    : endpoints.ltiStudentAssignment;
  let response: Response<StudentAssignment & { redirectTo: string }> =
    await request.get(generatePath(urlPath, { id }), {
      params: { ...getParams(), isStarted, lti_school_key: ltiSchoolKey },
    });

  if (response.data.data?.redirectTo) {
    await request.patch(response.data.data?.redirectTo);

    response = await request.get(generatePath(urlPath, { id, isStarted }), {
      params: { ...getParams(isStarted), lti_school_key: ltiSchoolKey },
    });
  }

  const { assignmentContentType } =
    response.data.data?.teacherSectionAssignment || {};

  if (assignmentContentType?.type === "assignmentContentTypeElement") {
    const elementPartsResponse = await request.get(
      generatePath(
        !isLti
          ? endpoints.studentAssignmentAssignedElementParts
          : endpoints.ltiStudentAssignmentAssignedElementParts,
        { id },
      ),
      {
        params: {
          include:
            "partType,partType.teacherOnly,measurementTable.measurementTableRows",
        },
      },
    );

    assignmentContentType.element = getActualElement(response);
    const elementParts: ElementPart[] = elementPartsResponse.data.data;
    assignmentContentType.element.elementParts = elementParts;
    assignmentContentType.element.elementPartIds = elementParts.map(
      (elementPart) => elementPart.id,
    );
  }

  return response;
};

export const fetchFullAssignment: FetchFullAssignment = ({
  id,
  profileType,
  callback,
  isStarted = false,
}) => {
  if (callback) {
    callback();
  }
  return Promise.all([
    fetchStudentAssignment(id, false, isStarted),
    ...(profileType === STUDENT
      ? [
          request.get(endpoints.startedAssignments, {
            params: {
              fields: {
                studentAssignment: "id,teacherSectionAssignment,autoSave",
                teacherSectionAssignment: "id,startOn,createdAt",
              },
              include: "teacherSectionAssignment",
            },
          }),
          request.get(endpoints.notStartedAssignments, {
            params: {
              fields: { teacherSectionAssignment: "id,startOn,autoSave" },
            },
          }),
          request.get(endpoints.actualDistrictAssignments, {
            params: { fields: { districtStudentAssignment: "id" } },
          }),
        ]
      : []),
  ]);
};

export const fetchFullDistrictAssignment: FetchFullDistrictAssignment = ({
  id,
  profileType,
  callback,
  districtAssignmentId,
  isStarted = false,
}) => {
  if (callback) {
    callback();
  }

  return Promise.all([
    request.get(
      generatePath(endpoints.districtStudentAssignment, { id, isStarted }),
      {
        params: {
          ...(districtAssignmentId
            ? { districtAssignmentId, isStarted }
            : { isStarted }),
          include:
            "districtAssignment.packageAssessment.assessmentBankQuestions,assignmentAnswers,student",
        },
      },
    ),
    ...(profileType === "Student"
      ? [
          request.get(endpoints.startedAssignments, {
            params: {
              fields: { studentAssignment: "id,name" },
            },
          }),
          request.get(endpoints.notStartedAssignments, {
            params: {
              fields: { teacherSectionAssignment: "id,name,startOn" },
            },
          }),
          request.get(endpoints.actualDistrictAssignments, {
            params: {
              fields: { districtStudentAssignment: "id,name" },
            },
          }),
        ]
      : []),
  ]);
};

export const fetchAssignmentsList = (
  callback?: () => void,
): Promise<JsonApiResponse[]> => {
  if (callback) {
    callback();
  }

  return Promise.all([
    request.get(endpoints.notStartedAssignments, {
      params: {
        include:
          "teacherSection,returnNotes,assignmentContentType,assignmentContentType.element",
        fields: {
          teacherSection: "id,name,teacherNames",
          element: "studentImage",
          assignmentContentTypeElement: "element",
          teacherSectionAssignment:
            "id,fullName,assignmentLabels,teacherSection,teacherNote,languageLabel," +
            "dueOn,startOn,accessibleDate,extendedAccess,thumbnailUrl,source,bilingual," +
            "returnNotes,districtMasterCalendarAssignmentKind,curriculumAreas,assignmentContentType,autoSave",
        },
      },
    }),
    request.get(endpoints.startedAssignments, {
      params: {
        include:
          "teacherSection,teacherSectionAssignment,teacherSectionAssignment.assignmentContentType,teacherSectionAssignment.assignmentContentType.element",
        fields: {
          teacherSection: "id,name,teacherNames,googleTeacherSection",
          element: "studentImage",
          assignmentContentTypeElement: "element",
          teacherSectionAssignment:
            "id,startOn,createdAt,accessibleDate,languageLabel,districtMasterCalendarAssignmentKind,curriculumAreas,assignmentContentTypeId,assignmentContentType",
          studentAssignment:
            "id,studentNote,teacherNote,assignmentLabels,fullName," +
            "teacherSection,teacherSectionAssignment,dueOn,startOn,extendedAccess,source,thumbnailUrl,bilingual,autoSave",
        },
      },
    }),
    request.get(endpoints.actualDistrictAssignments, {
      params: {
        include: "districtAssignment",
        fields: {
          districtStudentAssignment:
            "id,schoolName,startedAt,fullName,source,districtAssignment," +
            "endDate,timeToComplete,thumbnailUrl,name,curriculumAreaIds,status,note",
          districtAssignment: "id,name",
        },
      },
    }),
  ]);
};
